import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    memo,
    useRef,
} from 'react'
import { createPortal } from 'react-dom'
import unicornFxImage from '../../../assets/images/task/unicorn.png'

// antd
import {
    ConfigProvider,
    Typography,
    Input,
    message,
    Popover,
    Tooltip,
    Row,
    Col,
    Skeleton,
} from 'antd'
import { Icon } from '@iconify/react'

// slices
import {
    taskById,
    taskUpdateSupport,
    taskDeleteSupport,
    mainTaskById,
    mainTaskUpdate,
    mainTaskDelete,
    tasksState,
    selectTaskUpdate,
    refreshTaskHistory,
} from '../../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    isTaskMutationSuccess,
    resolveReferenceId,
} from '../../taskRefactored/tasks.service'
import EpicSelector from '../../taskRefactored/components/epicSelector'
import SprintSelector, {
    getSprintDisplayOptionFromTask,
} from '../../taskRefactored/components/sprintSelector'
import { getCachedProjectSprintOptions } from '../../taskRefactored/components/sprintSelector/sprintOptionsCache'
import { projectTaskListAssigneeUser } from '../../users/users.slice'
import AssigneeSelectorButton from './AssigneeSelectorButton'

// components
import DateRangePicker from './DateRangePicker'
import PrioritySelector from '../../taskRefactored/components/prioritySelector'
import StatusSelector from '../../taskRefactored/components/statusSelector'
import TypeSelector from '../../taskRefactored/components/typeSelector'
import ProjectSubTasksSection from './ProjectSubTasksSection'
import ProjectAttachmentSection from './ProjectAttachmentSection'
import ProjectCommentsTabs from './ProjectCommentsTabs'
import ProjectCommentInput from './ProjectCommentInput'
import TaskDescriptionEditor from './TaskDescriptionEditor'
import { confirmDeleteTask, getDeleteTaskMenuLabel } from './confirmDeleteTask'
import { useTaskListKindPermission } from '../projectPermissions'

// constants
import { priorityType } from '../../../constants/tasks.index'

// css
import styles from './TaskDetailView.module.css'
import {
    showActionNotification,
    NOTIFICATION_Z_ABOVE_FULLSCREEN,
} from '../../../components/notification'
import { PRIVATE_ROUTES, buildProjectTaskTabSearch } from '../../../routes'

const { TextArea } = Input
const TITLE_SAVE_DEBOUNCE_MS = 700
const TITLE_LIVE_UPDATE_DEBOUNCE_MS = 200
const TITLE_DISPLAY_DEBOUNCE_MS = 80
const { Text } = Typography
const TASK_TYPE_OPTIONS = [
    { label: 'Task', value: 'Task' },
    { label: 'Bug', value: 'Bug' },
]

const PROJECT_BOARD_VIEWS = ['board', 'list', 'roadmap']

const validateTaskTitle = (value) => {
    if (!value || value.trim() === '') {
        return 'Enter Task Title'
    }
    if (value.length > 400) {
        return 'Title should be maximum 400 characters long'
    }
    return ''
}

const TaskTitleEditor = memo(
    ({
        taskId,
        initialTitle,
        onSave,
        onLiveUpdate,
        onDisplayChange,
        inputClassName,
        errorClassName,
        readOnly = false,
    }) => {
        const [draft, setDraft] = useState(initialTitle || '')
        const [titleError, setTitleError] = useState('')
        const saveTimeoutRef = useRef(null)
        const liveUpdateTimeoutRef = useRef(null)
        const displayTimeoutRef = useRef(null)
        const lastSavedRef = useRef((initialTitle || '').trim())
        const isFocusedRef = useRef(false)
        const draftRef = useRef(initialTitle || '')

        useEffect(() => {
            draftRef.current = draft
        }, [draft])

        useEffect(() => {
            const nextTitle = initialTitle || ''
            if (!isFocusedRef.current) {
                setDraft(nextTitle)
                draftRef.current = nextTitle
                lastSavedRef.current = nextTitle.trim()
                setTitleError('')
            }
        }, [initialTitle, taskId])

        useEffect(
            () => () => {
                if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current)
                }
                if (liveUpdateTimeoutRef.current) {
                    clearTimeout(liveUpdateTimeoutRef.current)
                }
                if (displayTimeoutRef.current) {
                    clearTimeout(displayTimeoutRef.current)
                }
            },
            []
        )

        const flushLiveUpdate = useCallback(
            (value) => {
                onLiveUpdate?.(value)
            },
            [onLiveUpdate]
        )

        const scheduleLiveUpdate = useCallback(
            (value) => {
                if (liveUpdateTimeoutRef.current) {
                    clearTimeout(liveUpdateTimeoutRef.current)
                }
                liveUpdateTimeoutRef.current = setTimeout(() => {
                    flushLiveUpdate(value)
                }, TITLE_LIVE_UPDATE_DEBOUNCE_MS)
            },
            [flushLiveUpdate]
        )

        const scheduleDisplayChange = useCallback(
            (value) => {
                if (displayTimeoutRef.current) {
                    clearTimeout(displayTimeoutRef.current)
                }
                displayTimeoutRef.current = setTimeout(() => {
                    onDisplayChange?.(value)
                }, TITLE_DISPLAY_DEBOUNCE_MS)
            },
            [onDisplayChange]
        )

        const saveTitle = useCallback(
            async (value, { revertOnError = false } = {}) => {
                const trimmedTitle = value.trim()
                const error = validateTaskTitle(trimmedTitle)

                if (error) {
                    setTitleError(error)
                    if (revertOnError) {
                        const originalTitle = initialTitle || ''
                        setDraft(originalTitle)
                        draftRef.current = originalTitle
                        lastSavedRef.current = originalTitle.trim()
                        flushLiveUpdate(originalTitle)
                        onDisplayChange?.(originalTitle)
                    }
                    return
                }

                setTitleError('')
                if (trimmedTitle === lastSavedRef.current) return

                await onSave?.(trimmedTitle)
                lastSavedRef.current = trimmedTitle
            },
            [initialTitle, onSave, flushLiveUpdate, onDisplayChange]
        )

        const handleChange = (e) => {
            if (readOnly) return
            const value = e.target.value
            setDraft(value)
            draftRef.current = value
            scheduleLiveUpdate(value)
            scheduleDisplayChange(value)

            if (value.length > 400) {
                setTitleError('Title should be maximum 400 characters long')
            } else {
                setTitleError('')
            }

            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current)
                saveTimeoutRef.current = null
            }

            if (!value.trim() || value.length > 400) return

            saveTimeoutRef.current = setTimeout(() => {
                saveTitle(value)
            }, TITLE_SAVE_DEBOUNCE_MS)
        }

        const handleFocus = () => {
            isFocusedRef.current = true
        }

        const handleBlur = async () => {
            isFocusedRef.current = false
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current)
                saveTimeoutRef.current = null
            }
            if (liveUpdateTimeoutRef.current) {
                clearTimeout(liveUpdateTimeoutRef.current)
                liveUpdateTimeoutRef.current = null
            }
            if (displayTimeoutRef.current) {
                clearTimeout(displayTimeoutRef.current)
                displayTimeoutRef.current = null
            }
            flushLiveUpdate(draftRef.current)
            onDisplayChange?.(draftRef.current)
            await saveTitle(draftRef.current, { revertOnError: true })
        }

        return (
            <>
                <TextArea
                    value={draft}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={readOnly ? undefined : handleBlur}
                    readOnly={readOnly}
                    placeholder="Write a Task Title"
                    variant="borderless"
                    className={inputClassName}
                    status={titleError ? 'error' : ''}
                    autoSize={{
                        minRows: 1,
                        maxRows: 4,
                    }}
                    rows={1}
                />
                {titleError ? (
                    <div className={errorClassName}>{titleError}</div>
                ) : null}
            </>
        )
    }
)

TaskTitleEditor.displayName = 'TaskTitleEditor'

// CopyTextButton — clipboard API can fail inside fullscreen fixed layers; always fall back to execCommand
const copyTextToClipboard = async (text, mountNode) => {
    if (!text) return false

    const root =
        mountNode && mountNode instanceof HTMLElement
            ? mountNode
            : document.body

    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text)
            return true
        } catch {
            /* fall through to execCommand */
        }
    }

    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '0'
    ta.style.top = '0'
    ta.style.width = '2em'
    ta.style.height = '2em'
    ta.style.padding = '0'
    ta.style.border = 'none'
    ta.style.outline = 'none'
    ta.style.boxShadow = 'none'
    ta.style.background = 'transparent'
    ta.style.opacity = '0'
    ta.style.pointerEvents = 'none'
    root.appendChild(ta)
    ta.focus({ preventScroll: true })
    ta.select()
    ta.setSelectionRange(0, text.length)
    let ok = false
    try {
        ok = document.execCommand('copy')
    } finally {
        root.removeChild(ta)
    }
    return ok
}

const CopyTextButton = memo(
    ({ text, tooltipZIndex, copyMountRef, useFullscreenMount }) => {
        const handleCopy = useCallback(
            async (e) => {
                e.preventDefault()
                e.stopPropagation()
                if (useFullscreenMount) {
                    message.config({ zIndex: NOTIFICATION_Z_ABOVE_FULLSCREEN })
                }
                if (!text) {
                    message.warning('Link not ready yet')
                    return
                }
                const mountNode =
                    useFullscreenMount && copyMountRef?.current
                        ? copyMountRef.current
                        : null
                try {
                    const ok = await copyTextToClipboard(text, mountNode)
                    if (!ok) throw new Error('copy failed')
                    showActionNotification({
                        message: 'Link copied!',
                        subMessage: text,
                        notificationKey: 'link-copied',
                        placement: 'top',
                        zIndex: tooltipZIndex,
                    })
                } catch {
                    message.error('Failed to copy link')
                }
            },
            [text, tooltipZIndex, copyMountRef, useFullscreenMount]
        )

        return (
            <Tooltip
                title="Copy task link"
                zIndex={tooltipZIndex}
                getPopupContainer={() => document.body}
                destroyTooltipOnHide
            >
                <button
                    type="button"
                    className={styles.copyLinkBtn}
                    aria-label="Copy task link"
                    disabled={!text}
                    onMouseDown={handleCopy}
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                    }}
                >
                    <Icon
                        icon="qlementine-icons:link-16"
                        height={16}
                        width={16}
                    />
                </button>
            </Tooltip>
        )
    }
)

CopyTextButton.displayName = 'CopyTextButton'

const TaskDetailView = ({
    taskId,
    visible,
    onClose,
    projectId,
    filteredAssignees,
    onTaskUpdate,
    onTaskTitleLocalUpdate,
    onTaskClick,
    scrollToSection,
    detailSource = 'support',
    swimLaneOptions: swimLaneOptionsProp,
    projectTaskList: projectTaskListProp = [],
    isFullscreen = false,
    onToggleFullscreen,
}) => {
    const dispatch = useDispatch()
    const location = useLocation()
    const { id: routeProjectId, view: routeView } = useParams()
    const { selectedTask } = useSelector(tasksState)
    const isMainDetail = detailSource === 'main'
    const {
        canView: canViewModule,
        canEdit: canEditModule,
        canDelete: canDeleteModule,
    } = useTaskListKindPermission(isMainDetail ? 'main' : 'support')

    const [loading, setLoading] = useState(false)
    const [triggerCreateSubTask, setTriggerCreateSubTask] = useState(false)
    const [topBarTitle, setTopBarTitle] = useState('')
    const [description, setDescription] = useState('')
    const [dateRange, setDateRange] = useState({ start: null, end: null })
    const [priority, setPriority] = useState(null)
    const [status, setStatus] = useState(null)
    const [type, setType] = useState('Task')
    const [epic, setEpic] = useState(null)
    const [sprint, setSprint] = useState(null)

    // Comment state (shared with ProjectCommentInput and ProjectCommentsTabs)
    const [filteredMembers, setFilteredMembers] = useState([])
    const [mentionData, setMentionData] = useState({
        name: 'React',
        value: '',
        mentionData: null,
        mentionDataList: [],
        comment: '',
        users: [],
    })
    const [files, setFiles] = useState([])
    const [commentChange, setCommentChange] = useState([])
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [showFloatingTopBar, setShowFloatingTopBar] = useState(false)
    const [showCompletionCelebration, setShowCompletionCelebration] =
        useState(false)
    const commentsSectionRef = useRef(null)
    const subTasksSectionRef = useRef(null)
    const taskContentRef = useRef(null)
    const taskTitleRef = useRef(null)
    const taskDetailContainerRef = useRef(null)

    const swimLaneOptionsFromBoard = useMemo(() => {
        if (
            !Array.isArray(projectTaskListProp) ||
            projectTaskListProp.length === 0
        ) {
            return []
        }
        return projectTaskListProp.map((group) => ({
            label: group?._id,
            value: group?._id,
            color: group?.color || 'cyan',
        }))
    }, [projectTaskListProp])

    const swimLanesOption = useMemo(() => {
        if (swimLaneOptionsFromBoard.length > 0) return swimLaneOptionsFromBoard
        if (
            Array.isArray(swimLaneOptionsProp) &&
            swimLaneOptionsProp.length > 0
        ) {
            return swimLaneOptionsProp
        }
        return []
    }, [swimLaneOptionsFromBoard, swimLaneOptionsProp])

    useEffect(() => {
        if (!visible || !taskId) return undefined

        let cancelled = false
        setLoading(true)

        const load = async () => {
            try {
                if (isMainDetail) {
                    await dispatch(mainTaskById({ _id: taskId }))
                } else {
                    await dispatch(taskById({ _id: taskId }))
                }
            } catch (error) {
                if (!cancelled) {
                    message.error('Failed to load task details')
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }

        load()

        return () => {
            cancelled = true
        }
    }, [visible, taskId, isMainDetail, dispatch])

    useEffect(() => {
        setTriggerCreateSubTask(false)
        setTopBarTitle('')
    }, [taskId])

    const selectedTaskMatches = useMemo(() => {
        if (!taskId) return false
        const selectedId = selectedTask?.data?._id || selectedTask?._id
        return String(selectedId || '') === String(taskId)
    }, [selectedTask, taskId])

    useEffect(() => {
        if (!selectedTaskMatches || !selectedTask?.data?.comments) return
        setCommentChange(selectedTask.data.comments)
    }, [selectedTask, selectedTaskMatches])

    useEffect(() => {
        if (!selectedTaskMatches || !selectedTask?.data) return

        setDescription(selectedTask?.data?.description || '')
        setDateRange({
            start: selectedTask?.data?.start || null,
            end: selectedTask?.data?.end || null,
        })
        setPriority(selectedTask?.data?.priority || null)
        setType(selectedTask?.data?.type || 'Task')
        setEpic(
            isMainDetail
                ? resolveReferenceId(
                      selectedTask?.data?.epic ?? selectedTask?.data?.epicId
                  )
                : null
        )
        setSprint(
            isMainDetail
                ? resolveReferenceId(
                      selectedTask?.data?.sprintId ??
                          selectedTask?.data?.sprint
                  )
                : null
        )
    }, [selectedTask, isMainDetail, selectedTaskMatches])

    useEffect(() => {
        if (!selectedTaskMatches || !selectedTask?.data) return

        const taskStatus = selectedTask?.data?.status
        if (!taskStatus) {
            setStatus(null)
            return
        }

        if (swimLanesOption.length === 0) return

        const matchedStatus = swimLanesOption.find(
            (item) => item.label === taskStatus || item.value === taskStatus
        )
        if (matchedStatus) {
            setStatus(matchedStatus)
            return
        }

        setStatus({
            label: taskStatus,
            value: taskStatus,
            color: 'cyan',
        })
    }, [
        selectedTask?.data?.status,
        selectedTask?.data,
        swimLanesOption,
        selectedTaskMatches,
    ])

    const task = selectedTaskMatches ? selectedTask?.data : undefined
    const isDetailLoading = loading || (Boolean(taskId) && !selectedTaskMatches)

    const hasParentTaskForMenu = useMemo(() => {
        const parentTask = task?.parentTask
        if (parentTask) {
            if (Array.isArray(parentTask)) return parentTask.length > 0
            if (typeof parentTask === 'object') {
                return Object.keys(parentTask).length > 0
            }
            return Boolean(parentTask)
        }
        // Main / development tasks often return `parent` (id or populated object)
        return Boolean(
            resolveReferenceId(task?.parent ?? task?.parentId)
        )
    }, [task?.parentTask, task?.parent, task?.parentId])

    const parentTaskInfo = useMemo(() => {
        const parentTask = task?.parentTask
        if (Array.isArray(parentTask) && parentTask.length > 0) {
            return parentTask[0]
        }
        if (parentTask && typeof parentTask === 'object') {
            return parentTask
        }
        if (task?.parent && typeof task.parent === 'object') {
            return task.parent
        }
        return null
    }, [task?.parentTask, task?.parent])

    const parentTaskId = useMemo(
        () =>
            resolveReferenceId(parentTaskInfo) ||
            resolveReferenceId(task?.parent ?? task?.parentId),
        [parentTaskInfo, task?.parent, task?.parentId]
    )

    const parentTaskTitle = useMemo(() => {
        if (!parentTaskInfo) return 'Parent task'
        return (
            parentTaskInfo.title ||
            parentTaskInfo.name ||
            parentTaskInfo.label ||
            'Parent task'
        )
    }, [parentTaskInfo])

    const canAddSubtasks = !/^bug$/i.test(String(type || task?.type || 'Task').trim())

    const sprintDisplayOption = useMemo(
        () => (isMainDetail ? getSprintDisplayOptionFromTask(task) : null),
        [isMainDetail, task]
    )
    const isTypeDisabledForIssuesSprint = useMemo(() => {
        if (!isMainDetail) return false
        const normalize = (value) =>
            String(value || '')
                .trim()
                .toLowerCase()

        if (normalize(sprintDisplayOption?.label) === 'issues') return true

        const cachedSprintOptions =
            getCachedProjectSprintOptions(projectId) || []
        const fromCached = cachedSprintOptions.find(
            (item) => String(item?.value) === String(sprint ?? '')
        )
        if (normalize(fromCached?.label) === 'issues') return true

        const taskSprintLabel = task?.sprint?.title ?? task?.sprintId?.title
        return normalize(taskSprintLabel) === 'issues'
    }, [isMainDetail, projectId, sprintDisplayOption, sprint, task])

    // Scroll to comments section when scrollToSection is 'comments'
    useEffect(() => {
        if (
            scrollToSection === 'comments' &&
            !isDetailLoading &&
            selectedTaskMatches &&
            selectedTask?.data &&
            commentsSectionRef.current &&
            taskContentRef.current
        ) {
            // Use setTimeout to ensure the DOM is fully rendered
            setTimeout(() => {
                const commentsElement = commentsSectionRef.current
                const scrollableContainer = taskContentRef.current
                if (commentsElement && scrollableContainer) {
                    const elementTop =
                        commentsElement.offsetTop -
                        scrollableContainer.offsetTop
                    scrollableContainer.scrollTo({
                        top: elementTop - 20, // Add some offset from top
                        behavior: 'smooth',
                    })
                }
            }, 200)
        }
    }, [
        scrollToSection,
        isDetailLoading,
        selectedTask?.data,
        selectedTaskMatches,
    ])

    // Scroll to subtasks section when scrollToSection is 'subtasks'
    useEffect(() => {
        if (
            scrollToSection !== 'subtasks' ||
            isDetailLoading ||
            !selectedTaskMatches ||
            !selectedTask?.data ||
            !subTasksSectionRef.current ||
            !taskContentRef.current
        ) {
            return
        }
        setTimeout(() => {
            const subTasksElement = subTasksSectionRef.current
            const scrollableContainer = taskContentRef.current
            if (subTasksElement && scrollableContainer) {
                const elementTop =
                    subTasksElement.offsetTop - scrollableContainer.offsetTop
                scrollableContainer.scrollTo({
                    top: elementTop - 20,
                    behavior: 'smooth',
                })
            }
        }, 200)
    }, [
        scrollToSection,
        isDetailLoading,
        selectedTask?.data,
        selectedTaskMatches,
    ])

    // Exit fullscreen on Escape key
    useEffect(() => {
        if (!isFullscreen) return
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onToggleFullscreen?.()
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isFullscreen, onToggleFullscreen])

    // Open subtask composer when opened from board context menu
    useEffect(() => {
        if (
            scrollToSection !== 'add-subtasks' ||
            isDetailLoading ||
            !selectedTaskMatches ||
            !selectedTask?.data
        ) {
            return
        }
        if (hasParentTaskForMenu || !canAddSubtasks) return

        setTriggerCreateSubTask(true)
    }, [
        scrollToSection,
        isDetailLoading,
        selectedTask?.data,
        hasParentTaskForMenu,
        canAddSubtasks,
        selectedTaskMatches,
    ])

    useEffect(() => {
        const contentEl = taskContentRef.current
        if (!contentEl) return

        const updateFloatingBarVisibility = () => {
            const titleHeight = taskTitleRef.current?.offsetHeight || 0
            setShowFloatingTopBar(contentEl.scrollTop > titleHeight - 24)
        }

        updateFloatingBarVisibility()
        contentEl.addEventListener('scroll', updateFloatingBarVisibility, {
            passive: true,
        })

        return () => {
            contentEl.removeEventListener('scroll', updateFloatingBarVisibility)
        }
    }, [taskId, visible, isDetailLoading])

    const getInitials = (name) => {
        if (!name) return 'U'
        const parts = name.split(' ')
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase()
        }
        return name.substring(0, 2).toUpperCase()
    }

    const getAssigneeColor = (name) => {
        if (!name) return '#e2e8f0'
        const colors = [
            '#85BBF9',
            '#6366f1',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#06b6d4',
        ]
        const index = name.charCodeAt(0) % colors.length
        return colors[index]
    }

    const handleTitleLiveUpdate = useCallback(
        (value) => {
            if (onTaskTitleLocalUpdate && taskId) {
                onTaskTitleLocalUpdate(taskId, value)
            }
        },
        [onTaskTitleLocalUpdate, taskId]
    )

    const handleTitleDisplayChange = useCallback((value) => {
        setTopBarTitle(value)
    }, [])

    const handleDescriptionSave = async (html) => {
        if (html !== (task?.description || '')) {
            await updateTask({ description: html })
        }
    }

    const handleDateRangeChange = async (range) => {
        setDateRange(range)
        await updateTask({
            start: range.start,
            end: range.end,
        })
    }

    const handlePriorityChange = async (value) => {
        setPriority(value)
        await updateTask({ priority: value })
    }

    const handlePriorityUpdate = (name, value) => {
        setPriority(value)
        updateTask({ priority: value })
    }

    const handleStatusUpdate = (name, value) => {
        const statusValue = value?.label
        setStatus(value)
        updateTask({ status: statusValue })
    }

    const handleTypeUpdate = (name, value) => {
        if (hasParentTaskForMenu) return
        const typeValue = value || 'Task'
        setType(typeValue)
        updateTask({ type: typeValue })
    }

    const handleEpicChange = (epicId) => {
        if (hasParentTaskForMenu) return
        setEpic(epicId)
        updateTask({ epic: epicId })
    }

    const handleSprintChange = (sprintId) => {
        if (hasParentTaskForMenu) return
        setSprint(sprintId)
        updateTask({ sprintId })
    }

    const handleAssigneeChange = async (selected) => {
        dispatch(
            selectTaskUpdate({
                data: {
                    ...selectedTask?.data,
                    assigneeId: {
                        name: selected?.name,
                        _id: selected?._id,
                        email: selected?.email,
                        profilePicture: selected?.profilePicture,
                    },
                },
            })
        )

        await updateTask({
            assigneeId: selected?._id || null,
        })
    }

    const updateTask = async (updates) => {
        try {
            let result
            if (isMainDetail) {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    title:
                        updates.title !== undefined
                            ? updates.title
                            : task?.title || '',
                    assigneeId:
                        updates.assigneeId !== undefined
                            ? updates.assigneeId
                            : undefined,
                    priority:
                        updates.priority !== undefined
                            ? updates.priority
                            : task?.priority || 'Low',
                    status:
                        updates.status !== undefined
                            ? updates.status
                            : task?.status || 'To Do',
                    type:
                        updates.type !== undefined
                            ? updates.type
                            : task?.type || 'Task',
                    description:
                        updates.description !== undefined
                            ? updates.description
                            : task?.description || '',
                    start:
                        updates.start !== undefined
                            ? updates.start
                            : task?.start || null,
                    end:
                        updates.end !== undefined
                            ? updates.end
                            : task?.end || null,
                    isCompleted:
                        updates.isCompleted !== undefined
                            ? updates.isCompleted
                            : undefined,
                    epic: updates.epic !== undefined ? updates.epic : undefined,
                    sprintId:
                        updates.sprintId !== undefined
                            ? updates.sprintId
                            : undefined,
                })
                result = await dispatch(mainTaskUpdate({ id: taskId, body }))
            } else {
                const payload = {
                    _id: taskId,
                    updateTask: {
                        title:
                            updates.title !== undefined
                                ? updates.title
                                : task?.title || '',
                        projectId: projectId,
                        type:
                            updates.type !== undefined
                                ? updates.type
                                : task?.type || 'Task',
                        assigneeId:
                            updates.assigneeId !== undefined
                                ? updates.assigneeId
                                : task?.assigneeId?._id || null,
                        priority:
                            updates.priority !== undefined
                                ? updates.priority
                                : task?.priority || 'Low',
                        status:
                            updates.status !== undefined
                                ? updates.status
                                : task?.status || 'To Do',
                        description:
                            updates.description !== undefined
                                ? updates.description
                                : task?.description || '',
                        start:
                            updates.start !== undefined
                                ? updates.start
                                : task?.start || null,
                        end:
                            updates.end !== undefined
                                ? updates.end
                                : task?.end || null,
                        isCompleted:
                            updates.isCompleted !== undefined
                                ? updates.isCompleted
                                : (task?.isCompleted ?? undefined),
                    },
                }
                result = await dispatch(taskUpdateSupport(payload))
            }

            const data = result?.payload?.data
            if (isMainDetail ? isTaskMutationSuccess(data) : data?.success) {
                if (
                    updates.title !== undefined &&
                    selectedTask?.data &&
                    taskId
                ) {
                    dispatch(
                        selectTaskUpdate({
                            data: {
                                ...selectedTask.data,
                                title: updates.title,
                            },
                            _id: taskId,
                        })
                    )
                }
                dispatch(
                    projectTaskListAssigneeUser({
                        skip: 0,
                        limit: 100,
                        search: '',
                        projectId: projectId,
                        ...(isMainDetail ? { type: 'pms' } : {}),
                    })
                )
                if (taskId) {
                    dispatch(
                        refreshTaskHistory(
                            taskId,
                            isMainDetail ? 'main' : 'support'
                        )
                    )
                }
                if (onTaskUpdate) {
                    onTaskUpdate()
                }
            } else {
                message.error(data?.message || 'Failed to update task')
            }
        } catch (error) {
            message.error('Failed to update task')
        }
    }

    const updateTaskRef = useRef(updateTask)
    updateTaskRef.current = updateTask

    const saveTitleToServer = useCallback(async (trimmedTitle) => {
        await updateTaskRef.current({ title: trimmedTitle })
    }, [])

    const isTaskCompleted = Boolean(
        task?.isCompleted ??
        task?.isComplete ??
        ['completed', 'done'].includes(
            String(task?.status || '')
                .trim()
                .toLowerCase()
        )
    )

    const handleTopBarCompleteToggle = async () => {
        if (!canEditModule) return
        const nextCompleted = !isTaskCompleted
        await updateTask({ isCompleted: nextCompleted })
        if (nextCompleted) {
            setShowCompletionCelebration(true)
        }
    }

    // DELETE TASK
    const handleDeleteTask = async () => {
        try {
            const result = await dispatch(
                isMainDetail
                    ? mainTaskDelete(taskId)
                    : taskDeleteSupport(taskId)
            )
            const data = result?.payload?.data

            if (data) {
                const { success, message: checkMessage } = data
                const ok = isMainDetail ? isTaskMutationSuccess(data) : success
                if (ok) {
                    showActionNotification({
                        message: checkMessage || 'Task deleted successfully',
                        subMessage: task?.title || '',
                        actionButtonText: 'Undo',
                        notificationKey: 'task-deleted',
                    })
                    if (onTaskUpdate) {
                        onTaskUpdate()
                    }
                    if (onClose) {
                        onClose()
                    }
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            checkMessage || 'Could not delete task',
                        notificationKey: 'task-delete-failed',
                    })
                }
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Could not delete task',
                notificationKey: 'task-delete-failed',
            })
        }
    }

    const openDeleteTaskConfirm = () => {
        if (!canDeleteModule) return
        setPopoverOpen(false)
        confirmDeleteTask({
            type: selectedTask?.data?.type || type,
            onConfirm: handleDeleteTask,
        })
    }

    // Canonical task URL (must update when route gains :taskId — pathname-only memo was stale)
    const taskLink = useMemo(() => {
        const tid = selectedTask?._id || taskId
        if (!tid) return ''

        if (
            routeProjectId &&
            routeView &&
            PROJECT_BOARD_VIEWS.includes(routeView)
        ) {
            const path = PRIVATE_ROUTES.projects.projectIdViewTask(
                routeProjectId,
                routeView,
                tid
            )
            const qs = isMainDetail
                ? buildProjectTaskTabSearch('development')
                : ''
            return `${window.location.origin}${path}${qs}`
        }

        const p = location.pathname.replace(/\/$/, '') || ''
        if (p.endsWith(`/${tid}`)) {
            return `${window.location.origin}${p}`
        }
        return `${window.location.origin}${p}/${tid}`
    }, [
        selectedTask?._id,
        taskId,
        routeProjectId,
        routeView,
        location.pathname,
        isMainDetail,
    ])

    // z-index for Tooltip/Popover popups – must exceed the fullscreen overlay (9999)
    const tooltipZIndex = isFullscreen
        ? NOTIFICATION_Z_ABOVE_FULLSCREEN
        : undefined

    // When in fullscreen the container sits at z-index 9999. All antd popups
    // (Popover, Tooltip, Select, DatePicker, Modal …) default to ~1050 and are
    // hidden behind the overlay. Raising zIndexPopupBase to 10001 via
    // ConfigProvider context fixes every antd popup in the entire sub-tree
    // (AssigneeSelectorButton, DateRangePicker, PrioritySelector,
    //  StatusSelector, delete confirm, sub-task section, comments…)
    // without touching any of those files.
    const fullscreenTheme = useMemo(
        () => (isFullscreen ? { token: { zIndexPopupBase: 10001 } } : {}),
        [isFullscreen]
    )

    const handleAddSubtaskFromMenu = useCallback(() => {
        if (hasParentTaskForMenu || !canAddSubtasks) return
        setTriggerCreateSubTask(true)
        setPopoverOpen(false)
    }, [hasParentTaskForMenu, canAddSubtasks])

    // Memoize top bar icons to prevent flickering
    const topBarIcons = useMemo(() => {
        return (
            <>
                <CopyTextButton
                    text={taskLink}
                    tooltipZIndex={tooltipZIndex}
                    copyMountRef={taskDetailContainerRef}
                    useFullscreenMount={isFullscreen}
                />
                {isFullscreen ? (
                    <Tooltip
                        title="Toggle task panel"
                        placement="top"
                        zIndex={tooltipZIndex}
                    >
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={onToggleFullscreen}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    onToggleFullscreen?.()
                                }
                            }}
                            style={{
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            <Icon icon="uiw:shrink" height={16} width={16} />
                        </span>
                    </Tooltip>
                ) : (
                    <Tooltip
                        title="Full screen"
                        placement="top"
                        zIndex={tooltipZIndex}
                    >
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={onToggleFullscreen}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    onToggleFullscreen?.()
                                }
                            }}
                            style={{
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            <Icon
                                icon="pepicons-pencil:expand"
                                height={22}
                                width={22}
                            />
                        </span>
                    </Tooltip>
                )}
            </>
        )
    }, [
        taskLink,
        isFullscreen,
        onToggleFullscreen,
        tooltipZIndex,
    ])

    const showMoreMenu =
        (canEditModule && !hasParentTaskForMenu && canAddSubtasks) ||
        canDeleteModule

    const topBarActions = (
        <div className={styles.topBarRight}>
            {topBarIcons}
            {showMoreMenu && (
            <Popover
                arrow={false}
                content={
                    <div className={styles.moreMenu}>
                        {canEditModule &&
                        !hasParentTaskForMenu &&
                        canAddSubtasks ? (
                            <div className={styles.moreMenuSection}>
                                <button
                                    type="button"
                                    className={styles.moreMenuItem}
                                    onClick={handleAddSubtaskFromMenu}
                                >
                                    <Icon
                                        icon="cuida:subtask-outline"
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#757677',
                                            marginRight: 8,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>Add subtask</span>
                                </button>
                            </div>
                        ) : null}
                        {canDeleteModule && (
                        <div className={styles.moreMenuSection}>
                            <button
                                type="button"
                                className={`${styles.moreMenuItem} ${styles.moreMenuItemDanger}`}
                                onClick={openDeleteTaskConfirm}
                            >
                                <Icon
                                    icon="fluent:delete-20-regular"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#dc2626',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>{getDeleteTaskMenuLabel(type)}</span>
                            </button>
                        </div>
                        )}
                    </div>
                }
                trigger="click"
                placement="bottomRight"
                open={popoverOpen}
                onOpenChange={setPopoverOpen}
                zIndex={tooltipZIndex}
                classNames={{
                    root: styles.morePopover,
                }}
            >
                <Tooltip title="More" placement="top" zIndex={tooltipZIndex}>
                    <Icon
                        icon="ri:more-line"
                        height={20}
                        width={20}
                        style={{
                            cursor: 'pointer',
                        }}
                    />
                </Tooltip>
            </Popover>
            )}
            <Tooltip title="Close" placement="top" zIndex={tooltipZIndex}>
                <Icon
                    icon="ph:arrow-line-right"
                    onClick={onClose}
                    height={18}
                    width={18}
                    style={{
                        cursor: 'pointer',
                    }}
                />
            </Tooltip>
        </div>
    )

    const animatedTopTitleClass = `${styles.topBarAnimatedTitle} ${
        showFloatingTopBar ? styles.topBarAnimatedTitleVisible : ''
    }`

    if (!visible) {
        return null
    }

    return (
        <ConfigProvider theme={fullscreenTheme}>
            <>
                <div
                    ref={taskDetailContainerRef}
                    className={`${styles.taskDetailContainer} ${isFullscreen ? styles.taskDetailContainerFullscreen : ''}`}
                    style={{
                        height: '100%',
                    }}
                >
                    {isDetailLoading ? (
                        <>
                            <div className={styles.topBar}>
                                <div className={styles.topBarRight}>
                                    <Skeleton.Input
                                        active
                                        block
                                        style={{ height: 24, width: '30%' }}
                                    />
                                </div>
                            </div>

                            <div className={styles.taskContent}>
                                <div className={styles.taskTitle}>
                                    <div
                                        style={{
                                            width: '100%',
                                            padding: '15px 25px 0px 8px',
                                        }}
                                    >
                                        <Skeleton.Input
                                            active
                                            block
                                            style={{ height: 34, width: '88%' }}
                                        />
                                        <div style={{ marginTop: 10 }}>
                                            <Skeleton.Input
                                                active
                                                block
                                                style={{
                                                    height: 18,
                                                    width: '42%',
                                                }}
                                            />
                                        </div>
                                        <div style={{ marginTop: 10 }}>
                                            <Skeleton.Input
                                                active
                                                block
                                                style={{
                                                    height: 18,
                                                    width: '30%',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.collaboratorsSection}>
                                <Skeleton.Input
                                    active
                                    block
                                    style={{ height: 16, width: '100%' }}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.topBar}>
                                <div className={styles.topBarLeft}>
                                    {!isMainDetail && canEditModule && (
                                        <span
                                            className={`${styles.topBarMarkCompletePill} ${
                                                isTaskCompleted
                                                    ? styles.topBarMarkCompletePillDone
                                                    : ''
                                            } ${
                                                showFloatingTopBar
                                                    ? styles.topBarMarkCompletePillHidden
                                                    : ''
                                            }`}
                                            onClick={handleTopBarCompleteToggle}
                                        >
                                            <Icon icon="mdi:check" width={14} />
                                            {isTaskCompleted
                                                ? 'Completed'
                                                : 'Mark complete'}
                                        </span>
                                    )}
                                    <span className={animatedTopTitleClass}>
                                        {topBarTitle ||
                                            task?.title ||
                                            'Untitled task'}
                                    </span>
                                </div>
                                {topBarActions}
                            </div>
                            <div
                                className={styles.taskContent}
                                ref={taskContentRef}
                            >
                                <div
                                    className={styles.taskTitle}
                                    ref={taskTitleRef}
                                >
                                    <div
                                        style={{
                                            width: '100%',
                                            padding: '15px 25px 0px 8px',
                                        }}
                                    >
                                        <TaskTitleEditor
                                            taskId={taskId}
                                            initialTitle={task?.title || ''}
                                            onSave={saveTitleToServer}
                                            onLiveUpdate={handleTitleLiveUpdate}
                                            onDisplayChange={
                                                handleTitleDisplayChange
                                            }
                                            inputClassName={
                                                styles.taskTitleInput
                                            }
                                            errorClassName={styles.titleError}
                                            readOnly={!canEditModule}
                                        />
                                    </div>
                                </div>

                                <div className={styles.taskDetailSections}>
                                    <div className={styles.taskDetailSection}>
                                        <div className={styles.sectionLabel}>
                                            Assignee
                                        </div>
                                        <div className={styles.assigneeSection}>
                                            <div
                                                className={
                                                    styles.assigneeContent
                                                }
                                            >
                                                <AssigneeSelectorButton
                                                    selectedAssignee={
                                                        task?.assigneeId
                                                    }
                                                    onSelect={
                                                        canEditModule
                                                            ? handleAssigneeChange
                                                            : undefined
                                                    }
                                                    projectId={projectId}
                                                    filteredAssignees={
                                                        filteredAssignees
                                                    }
                                                    getAssigneeColor={
                                                        getAssigneeColor
                                                    }
                                                    getInitials={getInitials}
                                                    parentComponent="taskDetailView"
                                                    taskListKind={
                                                        detailSource === 'main'
                                                            ? 'main'
                                                            : 'support'
                                                    }
                                                />
                                                {task?.assigneeId &&
                                                Object.keys(task?.assigneeId)
                                                    .length > 0 ? (
                                                    <div
                                                        className={
                                                            styles.assigneeInfo
                                                        }
                                                    >
                                                        <Text
                                                            className={
                                                                styles.assigneeName
                                                            }
                                                        >
                                                            {
                                                                task.assigneeId
                                                                    .name
                                                            }
                                                        </Text>
                                                        {canEditModule && (
                                                            <Icon
                                                                icon="ic:round-close"
                                                                width={16}
                                                                height={16}
                                                                className={
                                                                    styles.removeAssigneeIcon
                                                                }
                                                                onClick={() =>
                                                                    handleAssigneeChange(
                                                                        null
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Text
                                                        className={
                                                            styles.noAssigneeText
                                                        }
                                                    >
                                                        No Assignee
                                                    </Text>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.taskDetailSection}>
                                        <div className={styles.sectionLabel}>
                                            Due date
                                        </div>
                                        <div className={styles.sectionContent}>
                                            <DateRangePicker
                                                value={dateRange}
                                                onChange={
                                                    canEditModule
                                                        ? handleDateRangeChange
                                                        : undefined
                                                }
                                                placeholder="No due date"
                                                parentComponent="taskDetailView"
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.taskDetailSection}>
                                        <div className={styles.sectionLabel}>
                                            Fields
                                        </div>
                                        <div
                                            style={{
                                                border: '1px solid #ededed',
                                                borderRadius: 8,
                                                width: '80%',
                                            }}
                                        >
                                            <Row
                                                style={{
                                                    borderBottom:
                                                        '1px solid #ededed',
                                                    padding: '8px 12px',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Col span={8}>
                                                    <span
                                                        style={{
                                                            color: '#595959',
                                                        }}
                                                    >
                                                        Priority
                                                    </span>
                                                </Col>
                                                <Col span={16}>
                                                    <PrioritySelector
                                                        value={priority}
                                                        onChange={
                                                            handlePriorityChange
                                                        }
                                                        priorityType={
                                                            priorityType
                                                        }
                                                        name="priority"
                                                        updateDetails={
                                                            handlePriorityUpdate
                                                        }
                                                        disabled={
                                                            !canEditModule
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                            {task && (
                                                <Row
                                                    style={{
                                                        padding: '8px 12px',
                                                        alignItems: 'center',
                                                        borderBottom:
                                                            '1px solid #ededed',
                                                    }}
                                                >
                                                    <Col span={8}>
                                                        <span
                                                            style={{
                                                                color: '#595959',
                                                            }}
                                                        >
                                                            Section
                                                        </span>
                                                    </Col>
                                                    <Col span={16}>
                                                        <StatusSelector
                                                            value={status}
                                                            variant="section"
                                                            placeholder="Select section"
                                                            statusType={
                                                                swimLanesOption
                                                            }
                                                            name="status"
                                                            updateDetails={
                                                                handleStatusUpdate
                                                            }
                                                            disabled={
                                                                !canEditModule
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                            {task && isMainDetail && (
                                                <Row
                                                    style={{
                                                        padding: '8px 12px',
                                                        alignItems: 'center',
                                                        ...(isMainDetail
                                                            ? {
                                                                  borderBottom:
                                                                      '1px solid #ededed',
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    <Col span={8}>
                                                        <span
                                                            style={{
                                                                color: '#595959',
                                                            }}
                                                        >
                                                            Type
                                                        </span>
                                                    </Col>
                                                    <Col span={16}>
                                                        <TypeSelector
                                                            value={type}
                                                            placeholder="Select type"
                                                            typeOptions={
                                                                TASK_TYPE_OPTIONS
                                                            }
                                                            name="type"
                                                            disabled={
                                                                hasParentTaskForMenu ||
                                                                isTypeDisabledForIssuesSprint
                                                            }
                                                            disabledInfoText={
                                                                hasParentTaskForMenu
                                                                    ? 'Inherited from parent task'
                                                                    : 'Type is locked for Issues sprint'
                                                            }
                                                            updateDetails={
                                                                handleTypeUpdate
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                            {task && isMainDetail && (
                                                <Row
                                                    style={{
                                                        padding: '8px 12px',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Col span={8}>
                                                        <span
                                                            style={{
                                                                color: '#595959',
                                                            }}
                                                        >
                                                            {hasParentTaskForMenu
                                                                ? 'Parent'
                                                                : 'Epic'}
                                                        </span>
                                                    </Col>
                                                    <Col span={16}>
                                                        {hasParentTaskForMenu ? (
                                                            <Tooltip
                                                                title="Open parent task"
                                                                placement="top"
                                                                zIndex={
                                                                    tooltipZIndex
                                                                }
                                                            >
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (
                                                                            parentTaskId &&
                                                                            onTaskClick
                                                                        ) {
                                                                            onTaskClick(
                                                                                parentTaskId
                                                                            )
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        display:
                                                                            'inline-flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 6,
                                                                        maxWidth:
                                                                            '100%',
                                                                        padding:
                                                                            '4px 10px',
                                                                        border: '1px solid #e2e8f0',
                                                                        borderRadius: 8,
                                                                        background:
                                                                            '#f8fafc',
                                                                        color: '#1e293b',
                                                                        fontSize: 13,
                                                                        fontWeight: 500,
                                                                        cursor: parentTaskId
                                                                            ? 'pointer'
                                                                            : 'default',
                                                                        textAlign:
                                                                            'left',
                                                                    }}
                                                                >
                                                                    <Icon
                                                                        icon="cuida:subtask-outline"
                                                                        width={
                                                                            16
                                                                        }
                                                                        height={
                                                                            16
                                                                        }
                                                                        style={{
                                                                            color: '#64748b',
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                    <span
                                                                        style={{
                                                                            overflow:
                                                                                'hidden',
                                                                            textOverflow:
                                                                                'ellipsis',
                                                                            whiteSpace:
                                                                                'nowrap',
                                                                        }}
                                                                    >
                                                                        {
                                                                            parentTaskTitle
                                                                        }
                                                                    </span>
                                                                </button>
                                                            </Tooltip>
                                                        ) : (
                                                            <EpicSelector
                                                                projectId={
                                                                    projectId
                                                                }
                                                                enabled={
                                                                    visible &&
                                                                    isMainDetail
                                                                }
                                                                placeholder="Select epic"
                                                                value={epic}
                                                                onChange={
                                                                    handleEpicChange
                                                                }
                                                            />
                                                        )}
                                                    </Col>
                                                </Row>
                                            )}
                                            {task && isMainDetail && (
                                                <Row
                                                    style={{
                                                        padding: '8px 12px',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Col span={8}>
                                                        <span
                                                            style={{
                                                                color: '#595959',
                                                            }}
                                                        >
                                                            Sprint
                                                        </span>
                                                    </Col>
                                                    <Col span={16}>
                                                        <SprintSelector
                                                            projectId={
                                                                projectId
                                                            }
                                                            enabled={
                                                                visible &&
                                                                isMainDetail
                                                            }
                                                            placeholder="Select sprint"
                                                            value={sprint}
                                                            displayOption={
                                                                sprintDisplayOption
                                                            }
                                                            disabled={
                                                                hasParentTaskForMenu
                                                            }
                                                            onChange={
                                                                handleSprintChange
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        className={styles.taskDetailSection}
                                        style={{
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <span className={styles.sectionLabel}>
                                            Description
                                        </span>
                                        <div
                                            style={{
                                                marginTop: '5px',
                                                width: '100%',
                                            }}
                                        >
                                            <TaskDescriptionEditor
                                                key={taskId}
                                                value={task?.description || ''}
                                                onSave={handleDescriptionSave}
                                                placeholder="What is this task about?"
                                                projectId={projectId}
                                                readOnly={!canEditModule}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div ref={subTasksSectionRef}>
                                    <ProjectSubTasksSection
                                        taskId={taskId}
                                        onTaskUpdate={onTaskUpdate}
                                        filteredAssignees={filteredAssignees}
                                        projectId={projectId}
                                        getAssigneeColor={getAssigneeColor}
                                        getInitials={getInitials}
                                        onTaskClick={onTaskClick}
                                        triggerCreateChild={
                                            triggerCreateSubTask
                                        }
                                        taskListKind={
                                            detailSource === 'main'
                                                ? 'main'
                                                : 'support'
                                        }
                                        readOnly={!canEditModule}
                                    />
                                </div>
                                <ProjectAttachmentSection
                                    taskId={taskId}
                                    onTaskUpdate={onTaskUpdate}
                                    taskListKind={
                                        detailSource === 'main'
                                            ? 'main'
                                            : 'support'
                                    }
                                    readOnly={!canEditModule}
                                />
                                {task && (
                                    <div ref={commentsSectionRef}>
                                        <ProjectCommentsTabs
                                            taskId={taskId}
                                            taskListKind={
                                                detailSource === 'main'
                                                    ? 'main'
                                                    : 'support'
                                            }
                                            setFilteredMembers={
                                                setFilteredMembers
                                            }
                                            mentionData={mentionData}
                                            files={files}
                                            setCommentChange={setCommentChange}
                                            commentChange={commentChange}
                                            onTaskUpdate={onTaskUpdate}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Comment Input Section — allowed with isRead (view), not only isEdit */}
                            {canViewModule && (
                            <div className={styles.collaboratorsSection}>
                                <ProjectCommentInput
                                    taskId={taskId}
                                    taskListKind={
                                        detailSource === 'main'
                                            ? 'main'
                                            : 'support'
                                    }
                                    projectId={projectId}
                                    onCommentAdded={onTaskUpdate}
                                    setCommentChange={setCommentChange}
                                    filteredMembers={filteredMembers}
                                    setFilteredMembers={setFilteredMembers}
                                    mentionData={mentionData}
                                    setMentionData={setMentionData}
                                    files={files}
                                    setFiles={setFiles}
                                />
                            </div>
                            )}
                        </>
                    )}
                </div>

                {showCompletionCelebration &&
                    typeof document !== 'undefined' &&
                    createPortal(
                        <div
                            className={styles.taskCompleteUnicornFx}
                            onAnimationEnd={() =>
                                setShowCompletionCelebration(false)
                            }
                        >
                            <img
                                className={styles.taskCompleteUnicornSvg}
                                src={unicornFxImage}
                                alt="Task completed celebration"
                                aria-hidden="true"
                            />
                        </div>,
                        document.body
                    )}
            </>
        </ConfigProvider>
    )
}

export default TaskDetailView
